import React from "react";
import { Box, Chip, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { Paperclip, Image as ImageIcon, FileText, AlertCircle, BookPlus } from "lucide-react";
import type { AttachmentStatus } from "../../../chat/hooks/useAttachments";

export interface AttachmentChipProps {
  name: string;
  status: AttachmentStatus;
  mime?: string;
  error?: string;
  onRemove?: () => void;
  /** When present and the attachment is `ready`, the chip surfaces an
   *  "add to knowledge base" affordance that calls this on click. */
  onPromote?: () => void;
  /** A short state shown after the name, e.g. "In knowledge base". */
  note?: string;
}

function icon(status: AttachmentStatus, mime?: string) {
  if (status === "uploading") return <CircularProgress size={13} thickness={4} sx={{ color: "text.secondary" }} />;
  if (status === "error") return <AlertCircle size={12} />;
  if (mime?.startsWith("image/")) return <ImageIcon size={12} />;
  if (mime) return <FileText size={12} />;
  return <Paperclip size={12} />;
}

export function AttachmentChip({ name, status, mime, error, onRemove, onPromote, note }: AttachmentChipProps) {
  const showPromote = !!onPromote && status === "ready";
  const baseText = status === "error" && error ? `${name} — ${error}` : name;
  const labelText = note ? `${baseText} · ${note}` : baseText;
  const label = showPromote ? (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
      {labelText}
      <Tooltip title="Add to knowledge base">
        <IconButton
          aria-label="add to knowledge base"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onPromote?.();
          }}
          sx={{ p: 0.25, color: "text.secondary" }}
        >
          <BookPlus size={13} />
        </IconButton>
      </Tooltip>
    </Box>
  ) : (
    labelText
  );

  const chip = (
    <Chip
      size="small"
      icon={icon(status, mime)}
      label={label}
      onDelete={onRemove}
      color={status === "error" ? "error" : "default"}
      variant={status === "error" ? "outlined" : "filled"}
      sx={{
        m: 0.5,
        maxWidth: 260,
        opacity: status === "uploading" ? 0.65 : 1,
        // The reason is the part of an error the user must read (e.g. the
        // upload limit), and it follows the file name — wrap rather than
        // ellipsize it out of view behind a long name.
        ...(status === "error" && {
          height: "auto",
          "& .MuiChip-label": { display: "block", whiteSpace: "normal", py: 0.25 },
        }),
      }}
    />
  );
  return status === "error" && error ? <Tooltip title={error}>{chip}</Tooltip> : chip;
}
